import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import {
  getLocations,
  getEmployees,
  createLocation,
  updateLocation,
  deactivateLocation,
} from "../../../../utils/services/control/masterDataService";
import LocationsDataGrid from "./LocationsDataGrid";
import LocationFormDialog from "./LocationFormDialog";
import ConfirmDialog from "../common/ConfirmDialog";
import useSnack from "../../hooks/useSnack";

const LocationsTab = () => {
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const { snack, showSnack, closeSnack } = useSnack();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterStatus !== "") filters.status = filterStatus;

      const [locData, empData] = await Promise.all([
        getLocations(filters),
        getEmployees({ status: 1 }),
      ]);
      setLocations(locData);
      setEmployees(empData);
    } catch (err) {
      console.error("Error loading locations:", err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredLocations = search.trim()
    ? locations.filter(
        (l) =>
          l.medicalCenter?.toLowerCase().includes(search.toLowerCase()) ||
          l.centerKey?.toLowerCase().includes(search.toLowerCase()) ||
          l.addressOffice?.toLowerCase().includes(search.toLowerCase())
      )
    : locations;

  const handleCreateOpen = () => {
    setEditLocation(null);
    setFormOpen(true);
  };

  const handleSave = async (data) => {
    setSaving(true);
    try {
      const isEdit = Boolean(data.locationId);
      const res = isEdit
        ? await updateLocation(data)
        : await createLocation(data);

      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack(
          isEdit
            ? "Location updated successfully"
            : "Location created successfully"
        );
        setFormOpen(false);
        loadData();
      }
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Error saving location",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setSaving(true);
    try {
      const res = await deactivateLocation(deactivateTarget.locationId);
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack("Location deactivated successfully");
        loadData();
      }
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Error deactivating location",
        "error"
      );
    } finally {
      setSaving(false);
      setDeactivateTarget(null);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          placeholder="Search locations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 240 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddLocationAltOutlinedIcon />}
          onClick={handleCreateOpen}
          size="small"
        >
          New Location
        </Button>
      </Box>

      <LocationsDataGrid
        rows={filteredLocations}
        loading={loading}
        onEdit={(loc) => {
          setEditLocation(loc);
          setFormOpen(true);
        }}
        onDeactivate={(loc) => setDeactivateTarget(loc)}
      />

      <LocationFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
        location={editLocation}
        employees={employees}
        loading={saving}
      />

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        title="Deactivate Location"
        message={`Deactivate ${deactivateTarget?.medicalCenter}? All employee assignments to this location will also be deactivated.`}
        confirmLabel="Deactivate"
        onConfirm={handleDeactivate}
        onCancel={() => setDeactivateTarget(null)}
        loading={saving}
      />

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack}>
        <Alert severity={snack.severity} onClose={closeSnack}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LocationsTab;
